import { forwardRef, useImperativeHandle } from "react";
import { Box, Button, IconButton, Paper } from "@mui/material";
import { Delete as DeleteIcon, AddCircleOutline as AddCircleOutlineIcon } from "@mui/icons-material";
import { FieldValues, useFieldArray, UseFormReturn } from "react-hook-form";
import { PRESCRIBED_SERVICES_MAX_NUMBER, getGroupOptionsServicesPrescribed, canUserEditData } from "../utils";
import { FormatedField } from "../../../../types/fields.types";
import { findField } from "../../../../utils/findField";
import { UserFromAPI } from "../../../../types/auth.types";
import { TreatmentPrescription, TreatmentPrescriptionForm } from "../types";
import { useServicesPrescribedFormHandler } from "../useServicesPrescribedFormHandler";
import { Wysiwyg } from "../../../../components/wysiwyg/Wysiwyg";
import { FieldName } from "../../../../components/groups/FieldName";
import { renderFieldInput } from "../../../../components/fieldsInputs/renderFieldInput";
import { getFieldExtraOption } from "../../../../utils/getFieldExtraOption";
import { useRecords } from "../../../../api/useRecords";

export type ServicePrescribedInputRef = {
	submitFn: (formData: TreatmentPrescriptionForm) => Promise<void>;
};

type ServicesPrescribedInputProps = {
	entityForm: UseFormReturn<FieldValues>;
	treatmentPrescription: TreatmentPrescription;
	fieldsTreatmentPrescription: Array<FormatedField>;
	fieldsServicesPrescribed: Array<FormatedField>;
	user: UserFromAPI;
};

export const ServicesPrescribedInput = forwardRef<
	ServicePrescribedInputRef,
	ServicesPrescribedInputProps
>(({ entityForm, treatmentPrescription, fieldsTreatmentPrescription, fieldsServicesPrescribed, user }, ref) => {

	const fieldWysiwyg = findField(fieldsTreatmentPrescription, "content");
	const { data: treatmentTherapyGoals } = useRecords("treatment_therapy_goals","id,service");

	const { submitFn } = useServicesPrescribedFormHandler(
		entityForm,
		treatmentPrescription,
		fieldsTreatmentPrescription,
		fieldsServicesPrescribed,
		treatmentTherapyGoals?.items ?? []
	);
	const servicesWithGoals = treatmentTherapyGoals?.items?.map((item:any) => item.service);

	const {
		fields: fieldsArray,
		append,
		remove,
	} = useFieldArray({
		control: entityForm.control,
		name: "services_prescribed",
	});

	const canEdit = canUserEditData(treatmentPrescription?.services_prescribed?.[0], treatmentPrescription?.treatment?.sessions, user)

	useImperativeHandle(ref, () => ({
		submitFn,
	}));

	return (
		<Box display="flex" flexDirection="column" gap={6} flexWrap={"wrap"}>
			<Box display="flex" flexDirection="column" gap={4}>
				<Paper
					elevation={2}
					sx={{
						p: 4,
						borderRadius: 4,
						minWidth: "350px",
					}}
				>
				{fieldsArray.map((field, index) => {
					const prefixedFields = fieldsServicesPrescribed.map((field) => ({
						...field,
						originalFieldName: field.field,
						field: `services_prescribed.${index}.${field.field}`,
					}));
					const currentServiceId = entityForm.watch(`services_prescribed.${index}.service`);
					const groupOptions = getGroupOptionsServicesPrescribed(index, canEdit, servicesWithGoals, currentServiceId?.id);
					return (
							<Box
								key={field.id}
								sx={{
									display: "flex",
									flex: 1,
									flexDirection: "row",
									alignItems: "flex-start",
									justifyContent: "flex-start",
									gap: 4,
									maxWidth: {xs:"100%", sm:"500px", md:"1450px"},
								}}
							>
								{prefixedFields
									.filter((item) => item.settings.group)
									.map((prefixedField) => {
										const fieldExtraOptions = getFieldExtraOption(
											groupOptions.fieldsWithExtraOptions ?? [],
											prefixedField
										);
										const servicePrescribedFieldRender = findField(
											fieldsServicesPrescribed,
											prefixedField.originalFieldName
										);
										const isService = prefixedField.originalFieldName === "service";
										const isEstimatedSessions = prefixedField.originalFieldName === "estimated_total_sessions";
										const isGoals = prefixedField.originalFieldName === "goals";

										let boxStyles = { display: "flex", flex: 1, mb: 3 } as any
										if (isService) {
											boxStyles = { ...boxStyles, minWidth:"250px", maxWidth:"250px"};
										} else if (isEstimatedSessions) {
											boxStyles = { ...boxStyles, minWidth:"150px", maxWidth:"150px"};
										} else if (isGoals) {
											boxStyles = { ...boxStyles, minWidth:"200px"};
										}
										return (
											<Box key={prefixedField.field} sx={boxStyles}>
												{renderFieldInput(
													servicePrescribedFieldRender,
													treatmentPrescription?.services_prescribed?.[index],
													"group",
													entityForm as unknown as UseFormReturn<FieldValues>,
													fieldExtraOptions
												)}
											</Box>
										);
									})}
								<IconButton
									onClick={() => remove(index)}
									color="error"
									sx={{
										mb: 3,
									}}
									size="small"
									disabled={!canEdit}
								>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</Box>
						);
					})}
					{fieldsArray.length < PRESCRIBED_SERVICES_MAX_NUMBER && (
						<Box display="flex" justifyContent="center">
							<Button
								onClick={() => append({})}
								color="primary"
								size="large"
								sx={{ mb: 4 }}
								disabled={!canEdit}
								startIcon={<AddCircleOutlineIcon />}
							>
								Adicionar serviço
							</Button>
						</Box>
					)}
		</Paper>
		<Box>

			<FieldName sx={{ mb: 2 }} headerName="Informações Adicionais" />
					<Wysiwyg
						entityForm={entityForm}
						initialValue={treatmentPrescription?.content}
						size="130px"
						field={fieldWysiwyg}
						canMention={false}
						isDisabled={!canEdit}
					/>
				</Box>
			</Box>
		</Box>
	);
});
